import React from 'react';
import { getTeeColor, teeNeedsOutline } from '../utils/teeColors';
import './PlayerTeeChip.css';

/**
 * One player's tee, as a compact tappable chip.
 *
 * A dropdown inline on a group card ate the name it sat beside — at phone width
 * a group card is half the screen, and a `<select>` wide enough to read "Purple"
 * left one letter of "David Bavin". A colour dot plus the tee's name says the
 * same thing in a fraction of the width, and the full choice — with each tee's
 * rating and slope — moves to a sheet where there is room for it.
 *
 * Two shapes, because the two places it appears are not the same size:
 *  - the full pill, colour + the tee's REAL name, for a full-width row
 *    (PlayerTeeList) where there is room to read it;
 *  - `compact`, JUST THE COLOURED CIRCLE, for a group card — two cards to a
 *    phone row, where a pill of any kind is a second object competing with the
 *    player's name for a hundred-odd pixels. The circle keeps a finger-sized
 *    target around it, names the tee to a screen reader and on hover, and
 *    opens the same sheet, which spells every tee out.
 */
export const PlayerTeeChip = ({ teeName, disabled, compact = false, onClick }) => {
  const color = getTeeColor(teeName);
  return (
    <button
      type="button"
      className={`player-tee-chip${compact ? ' player-tee-chip--dot' : ''}`}
      disabled={disabled}
      title={compact ? (teeName || 'Set tee') : undefined}
      aria-label={`Tee: ${teeName || 'not set'}. Tap to change.`}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    >
      <span
        className={`player-tee-chip__dot${teeNeedsOutline(teeName) ? ' player-tee-chip__dot--outlined' : ''}`}
        style={{ backgroundColor: color || '#ffffff' }}
      />
      {!compact && <span className="player-tee-chip__label">{teeName || 'Set tee'}</span>}
    </button>
  );
};

/**
 * The sheet the chip opens: which tee does this player drive from?
 *
 * Ratings are shown because they are the reason the choice matters — a tee's
 * Course Rating and Slope are what produce that player's handicap.
 */
export const PlayerTeeSheet = ({ isOpen, playerName, tees = [], selectedTeeId, onSelect, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content player-tee-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Tee</h2>
          <p>Which tee is <strong>{playerName}</strong> playing from?</p>
        </div>
        <div className="player-tee-sheet__list">
          {tees.map(tee => {
            const color = getTeeColor(tee.name);
            const active = tee.id === selectedTeeId;
            return (
              <button
                key={tee.id}
                type="button"
                className={`player-tee-sheet__btn${active ? ' player-tee-sheet__btn--active' : ''}`}
                onClick={() => { onSelect(tee.id); onClose(); }}
              >
                <span className="player-tee-sheet__left">
                  <span
                    className={`tee-color-dot${teeNeedsOutline(tee.name) ? ' tee-color-dot--white' : ''}`}
                    style={{ backgroundColor: color || '#ffffff' }}
                  />
                  <span className="player-tee-sheet__name">{tee.name}</span>
                </span>
                <span className="player-tee-sheet__ratings">
                  CR {tee.course_rating ?? '-'} · SL {tee.slope_rating ?? '-'}
                </span>
              </button>
            );
          })}
        </div>
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default PlayerTeeChip;
